import { cartPricesAsOf } from '@share-a-cart/shared-utils';
import { UseQueryResult } from '@tanstack/react-query';
import { useState } from 'react';
import {
  CartBaseNotes,
  CartItemsList,
  GetReviewFooter,
  GetReviewHeader,
  GetReviewMissingItems,
  GetReviewOwnership,
  RequestApprovalModal,
} from 'src/components';
import type { CartAmazonDeal, CartGetData, CartGetItem } from 'src/types';
import { useApprovalResponse } from './useApprovalResponse';

type Props = {
  cartId: string;
  cartQuery: UseQueryResult<CartGetData, Error>;
  dealForItem?: (item: CartGetItem) => CartAmazonDeal | null;
};

export const ReviewCart = ({ cartId, cartQuery, dealForItem }: Props) => {
  const [showRequestApprovalModal, setShowRequestApprovalModal] =
    useState(false);

  const {
    handleApproveOrReject,
    lottieRefHeader,
    lottieRefFooter,
    isDirty,
    resetIsDirty,
    // approvalStatus,
  } = useApprovalResponse({ cartId, setShowRequestApprovalModal });

  return (
    <>
      <GetReviewHeader
        ref={lottieRefHeader}
        handleApproveOrReject={handleApproveOrReject}
        cartId={cartId}
        cartTotalQty={cartQuery.data?.cartTotalQty || 0}
        pricesAsOf={cartPricesAsOf(cartQuery.data) ?? 0}
        cartTitle={cartQuery.data?.title || undefined}
        cartPrice={cartQuery.data?.formattedCartTotalPrice || '$0.00 USD'}
        cartVendor={cartQuery.data?.vendor!}
        cartVendorDisplayName={cartQuery.data?.vendorDisplayName!}
        cartCreated={cartQuery.data?.ownershipData?.created || false}
        cartStore={cartQuery.data?.store || undefined}
        displaySourceSite={
          cartQuery.data &&
          cartQuery.data.displaySourceSite &&
          cartQuery.data.displaySourceSite in cartQuery.data
            ? (cartQuery.data[
                cartQuery.data.displaySourceSite as keyof CartGetData
              ] as string)
            : undefined
        }
        isLoading={cartQuery.isLoading}
        page={'review'}
        orgApprovalStatus={
          cartQuery.data?.approvalDetails?.at(-1)?.status || undefined
        }
        requestedByEmail={
          cartQuery.data?.approvalDetails?.at(-1)?.requestedBy?.email || ''
        }
        whitePendingStatus={true}
      />
      {cartQuery.data?.ownershipData && !cartQuery.isLoading && (
        <GetReviewOwnership
          cartTimestamp={cartQuery.data.timestamp || 0}
          cartListed={cartQuery.data.ownershipData?.listed || false}
          cartNotes={cartQuery.data.ownershipData?.note || ''}
          cartTags={cartQuery.data.ownershipData?.tags || []}
          cartTitle={cartQuery.data.title || undefined}
          cartCreated={cartQuery.data.ownershipData?.created || false}
          cartStore={cartQuery.data?.store || undefined}
          cartCCY={cartQuery.data?.cartCCY || 'USD'}
          cartCCYS={cartQuery.data?.cartCCYS || '$'}
          cartItems={cartQuery.data?.items || []}
          displayTrashIcon={false}
        />
      )}
      <GetReviewMissingItems
        missingCount={cartQuery.data?.missingCount || 0}
        cartItemsLength={cartQuery.data?.items.length || 1}
      />
      <CartItemsList
        cartItems={cartQuery.data?.items}
        cartCCY={cartQuery.data?.cartCCY}
        cartCCYS={cartQuery.data?.cartCCYS}
        cartVendor={cartQuery.data?.vendor!}
        cartLocale={cartQuery.data?.locale}
        page={'review'}
        dealForItem={dealForItem}
      />
      <CartBaseNotes notes={cartQuery.data?.note} border />
      <GetReviewFooter
        ref={lottieRefFooter}
        handleApproveOrReject={handleApproveOrReject}
        ccy={cartQuery.data?.cartCCY || 'USD'}
        ccyS={cartQuery.data?.cartCCYS || '$'}
        cartPrice={cartQuery.data?.formattedCartTotalPrice || '$0.00 USD'}
        totalQty={cartQuery.data?.cartTotalQty || 0}
        cartTitle={cartQuery.data?.title || undefined}
        cartItems={cartQuery.data?.items || []}
        cartTimestamp={cartQuery.data?.timestamp || 0}
        cartStore={cartQuery.data?.store || undefined}
        isLoading={cartQuery.isLoading}
        page={'review'}
        orgApprovalStatus={
          cartQuery.data?.approvalDetails?.at(-1)?.status || undefined
        }
        requestedByEmail={
          cartQuery.data?.approvalDetails?.at(-1)?.requestedBy?.email || ''
        }
      />

      <RequestApprovalModal
        cartId={cartId}
        show={showRequestApprovalModal}
        setShow={setShowRequestApprovalModal}
        isDirty={isDirty}
        resetIsDirty={resetIsDirty}
      />
    </>
  );
};
